/* Modulo: Reviews Overlay — overlay review card rail.
   Vanilla IIFE. Own idempotent init + theme-editor lifecycle (no window.moduloLazyInit).
   Reduced-motion aware. */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }

  function initSection(el) {
    if (!el || el.__toInit) return;
    var track = el.querySelector('[data-modulo-to-track]');
    if (!track) return;
    el.__toInit = true;

    var prev = el.querySelector('[data-modulo-to-prev]');
    var next = el.querySelector('[data-modulo-to-next]');
    var cards = function () { return track.querySelectorAll('.modulo-to__card'); };

    var stepMode = el.getAttribute('data-step-mode') === 'single' ? 'single' : 'page';
    var page = 0;
    var timer = null;
    var autoplayOn = el.getAttribute('data-autoplay') === 'true' && !REDUCE;
    var speed = parseFloat(el.getAttribute('data-autoplay-speed'));
    if (!speed || isNaN(speed)) speed = 6;

    function viewportWidth() { return window.innerWidth || document.documentElement.clientWidth; }

    function cardsPerView() {
      var w = viewportWidth();
      var attr;
      if (w <= 480) attr = 'data-cpv-mobile';
      else if (w <= 860) attr = 'data-cpv-tablet';
      else attr = 'data-cpv-desktop';
      var n = parseInt(el.getAttribute(attr), 10);
      return n && !isNaN(n) ? n : 3;
    }

    function applyCpv() { el.style.setProperty('--modulo-to-cpv', cardsPerView()); }

    function pageCount() {
      var total = cards().length;
      var cpv = cardsPerView();
      if (total <= cpv) return 1;
      if (stepMode === 'single') return Math.max(1, total - cpv + 1);
      return Math.max(1, Math.ceil(total / cpv));
    }

    function goTo(p, animate) {
      var pages = pageCount();
      page = clamp(p, 0, pages - 1);

      var first = cards()[0];
      if (!first) return;
      var cardW = first.getBoundingClientRect().width;
      var gap = parseFloat(getComputedStyle(track).gap) || 0;
      var stride = stepMode === 'single' ? 1 : cardsPerView();
      var total = cards().length;
      var maxCards = Math.max(0, total - cardsPerView());
      var advance = Math.min(page * stride, maxCards);
      var offset = advance * (cardW + gap);

      if (!animate || REDUCE) {
        var prevTransition = track.style.transition;
        track.style.transition = 'none';
        track.style.transform = 'translate3d(' + (-offset) + 'px, 0, 0)';
        track.offsetHeight; /* reflow so the jump isn't animated */
        track.style.transition = prevTransition;
      } else {
        track.style.transform = 'translate3d(' + (-offset) + 'px, 0, 0)';
      }

      if (prev) prev.disabled = page === 0;
      if (next) next.disabled = page >= pages - 1;
    }

    // ─── Autoplay ───
    function startAutoplay() {
      if (!autoplayOn || timer) return;
      timer = window.setInterval(function () {
        if (!el.isConnected) { stopAutoplay(); return; } // self-heal if section was re-rendered
        var pages = pageCount();
        goTo(page + 1 >= pages ? 0 : page + 1, true);
      }, speed * 1000);
    }
    function stopAutoplay() {
      if (timer) { window.clearInterval(timer); timer = null; }
      autoplayOn = false;
    }

    // ─── Handlers (kept referenced so teardown can remove them) ───
    function onPrev() { stopAutoplay(); goTo(page - 1, true); }
    function onNext() { stopAutoplay(); goTo(page + 1, true); }
    if (prev) prev.addEventListener('click', onPrev);
    if (next) next.addEventListener('click', onNext);

    var SWIPE = 40;
    var startX = null, startY = null;
    function onDown(e) {
      if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
      startX = e.clientX; startY = e.clientY;
    }
    function onUp(e) {
      if (startX === null) return;
      var dx = e.clientX - startX, dy = e.clientY - startY;
      if (Math.abs(dx) > SWIPE && Math.abs(dx) > Math.abs(dy)) {
        stopAutoplay();
        goTo(dx < 0 ? page + 1 : page - 1, true);
      }
      startX = null; startY = null;
    }
    function onCancel() { startX = null; startY = null; }
    track.addEventListener('pointerdown', onDown);
    track.addEventListener('pointerup', onUp);
    track.addEventListener('pointercancel', onCancel);

    function onEnter() { if (timer) { window.clearInterval(timer); timer = null; } }
    function onLeave() { startAutoplay(); }
    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);

    var rt;
    function onResize() {
      if (!el.isConnected) { window.removeEventListener('resize', onResize); return; } // self-heal leak
      window.clearTimeout(rt);
      rt = window.setTimeout(function () {
        applyCpv();
        requestAnimationFrame(function () { goTo(Math.min(page, pageCount() - 1), false); });
      }, 100);
    }
    window.addEventListener('resize', onResize);

    // ─── Teardown (theme-editor unload / re-render) ───
    el.__toTeardown = function () {
      stopAutoplay();
      window.clearTimeout(rt);
      window.removeEventListener('resize', onResize);
      if (prev) prev.removeEventListener('click', onPrev);
      if (next) next.removeEventListener('click', onNext);
      track.removeEventListener('pointerdown', onDown);
      track.removeEventListener('pointerup', onUp);
      track.removeEventListener('pointercancel', onCancel);
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
      el.__toInit = false;
    };

    // ─── Boot ───
    applyCpv();
    requestAnimationFrame(function () {
      goTo(0, false);
      startAutoplay();
    });
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var els = scope.querySelectorAll('[data-modulo-to]');
    for (var i = 0; i < els.length; i++) initSection(els[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* ─── Theme editor lifecycle ─── */
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target;
    if (!el) return;
    if (el.matches && el.matches('[data-modulo-to]')) {
      if (el.__toTeardown) el.__toTeardown();
      el.__toInit = false;
      initSection(el);
    } else {
      initAll(el);
    }
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target;
    if (!el) return;
    if (el.matches && el.matches('[data-modulo-to]')) {
      if (el.__toTeardown) el.__toTeardown();
    } else if (el.querySelectorAll) {
      var inner = el.querySelectorAll('[data-modulo-to]');
      for (var i = 0; i < inner.length; i++) if (inner[i].__toTeardown) inner[i].__toTeardown();
    }
  });
})();
